import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { SWRConfig } from 'swr';
import './styles/globals.css';
import App from './App.tsx';
import { swrConfig } from './lib/swr-config';
import { env } from './lib/env';
import {
  initializeErrorTracking,
  initializePerformanceMonitoring,
  initializeAnalytics,
  setupGlobalErrorHandling,
} from './lib/error-tracking';

async function enableMocking() {
  // Use environment configuration to determine if MSW should be enabled
  if (!env.features.enableMSW) {
    return;
  }

  const { worker } = await import('./mocks/browser');

  return worker.start({
    onUnhandledRequest: 'bypass',
  });
}

// Initialize the application
async function initializeApp() {
  // Initialize monitoring for production
  if (env.isProduction) {
    // Set up global error handling
    setupGlobalErrorHandling();

    // Initialize error tracking
    initializeErrorTracking();

    // Initialize performance monitoring
    initializePerformanceMonitoring();

    // Initialize analytics (production only)
    if (env.isProduction) {
      initializeAnalytics();
    }
  }

  // Enable mocking if configured
  await enableMocking();

  // Log initialization in development
  if (env.isDevelopment && env.features.enableDebugLogging) {
    console.log('🚀 CareCal Frontend initialized', {
      mode: env.mode,
      features: env.features,
      mswEnabled: env.features.enableMSW,
    });
  }

  // Render the application
  const AppComponent = env.dev.enableStrictMode ? (
    <StrictMode>
      <SWRConfig value={swrConfig}>
        <App />
      </SWRConfig>
    </StrictMode>
  ) : (
    <SWRConfig value={swrConfig}>
      <App />
    </SWRConfig>
  );

  createRoot(document.getElementById('root')!).render(AppComponent);
}

// Start the application
initializeApp().catch(error => {
  console.error('Failed to initialize application:', error);

  // Fallback: render a basic error message
  createRoot(document.getElementById('root')!).render(
    <div
      style={{
        padding: '2rem',
        textAlign: 'center',
        fontFamily: 'system-ui, sans-serif',
        color: '#dc2626',
      }}
    >
      <h1>Application Error</h1>
      <p>Failed to initialize the application. Please refresh the page.</p>
      <details style={{ marginTop: '1rem', textAlign: 'left' }}>
        <summary>Error Details</summary>
        <pre
          style={{
            background: '#f3f4f6',
            padding: '1rem',
            borderRadius: '0.5rem',
            overflow: 'auto',
            fontSize: '0.875rem',
          }}
        >
          {error instanceof Error ? error.stack : String(error)}
        </pre>
      </details>
    </div>
  );
});
